import {useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import classNames from 'classnames'
import SubscriptionTabs from './SubscriptionTabs'
import TextHelper from '../../../utils/text'
import './index.scss'
import {createSubscriptionSession, getSubscriptionPayments} from '../../../stores/payment'
import SubscriptionItem from './SubscriptionItem'

const SUB_STATUSES = {
    ALL: 'all',
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    PAYMENT_PENDING: 'payment_pending',
    PAYMENT_FAILED: 'payment_failed',
    CANCELED: 'canceled'
}

const sortByDateDesc = (subscriptions) => {
    return subscriptions?.toSorted((a, b) => new Date(b?.created_at) - new Date(a?.created_at))
}

const SubscriptionList = ({
    allSubscriptions = [],
    paymentData,
    isPaymentFetching,
    text,
    processingId,
    onActivate,
    filteredSubscriptions,
    setFilteredSubscriptions,
    onPrepareCancelSub,
    isDarkMode
}) => {
    const dispatch = useDispatch()

    const [expandedSubId, setExpandedSubId] = useState(null)
    const [activeTab, setActiveTab] = useState(SUB_STATUSES.ACTIVE)

    const tabs = useMemo(() => [
        {key: SUB_STATUSES.ALL, label: text.all},
        {key: SUB_STATUSES.ACTIVE, label: text.active},
        {key: SUB_STATUSES.INACTIVE, label: text.inactive}
    ], [text])

    const toggleSubscriptionExpanded = (subId) => {
        setExpandedSubId(prev => prev === subId ? null : subId)
    }

    const handleTabChange = (tab) => {
        setActiveTab(prev => tab === prev ? SUB_STATUSES.ALL : tab)
    }

    const handleUpdatePayment = (subId) => {
        dispatch(createSubscriptionSession(subId))
    }

    useEffect(() => {
        setExpandedSubId(null)

        if (activeTab === SUB_STATUSES.ALL) {
            setFilteredSubscriptions(allSubscriptions)
            return
        }

        const filtered = allSubscriptions?.filter(sub =>
            activeTab === SUB_STATUSES.INACTIVE ? !sub?.active : sub?.active
        )
        setFilteredSubscriptions(filtered)
    }, [activeTab, allSubscriptions, setFilteredSubscriptions])

    useEffect(() => {
        if (expandedSubId) {
            dispatch(getSubscriptionPayments(expandedSubId))
        }
    }, [expandedSubId, dispatch])

    const sortedSubscriptions = sortByDateDesc(filteredSubscriptions)
    const hasSubscriptions = sortedSubscriptions?.length > 0

    return (
        <div className={classNames('subscription-list', {dark: isDarkMode})}>
            <SubscriptionTabs
                text={text}
                onChangeTabs={handleTabChange}
                activeTab={activeTab}
                tabs={tabs}
                isDarkMode={isDarkMode}
            />

            {hasSubscriptions ? (
                sortedSubscriptions.map(subscription => (
                    <SubscriptionItem
                        key={subscription.subscription_id}
                        subscription={subscription}
                        text={text}
                        subStatuses={SUB_STATUSES}
                        isExpanded={expandedSubId === subscription.subscription_id}
                        processingId={processingId}
                        paymentData={paymentData}
                        isPaymentFetching={isPaymentFetching}
                        onToggleExpand={() => toggleSubscriptionExpanded(subscription.subscription_id)}
                        onActivate={onActivate}
                        onUpdatePayment={() => handleUpdatePayment(subscription.subscription_id)}
                        onCancel={onPrepareCancelSub}
                    />
                ))
            ) : (
                <span className="no-filtered-subscriptions">
                    {TextHelper.replaceAllChars(text.no_filtered_subscriptions, ':active_tab', activeTab)}
                </span>
            )}
        </div>
    )
}

export default SubscriptionList
